import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ImageUploadButton from '../../components/ImageUploadButton';

// Selector de opciones/modificadores integrado DENTRO del formulario del producto.
// El admin marca qué grupos aplican a este plato y, para cada uno, qué opciones
// puntuales ofrece (ej: este jugo no tiene "sirope de vainilla") y si es obligatorio.
// Todo se guarda junto con el producto en un solo paso — no hay pantallas separadas.
const ProductModifiersPicker = ({ allModifiers, selections, onChange }) => {
  if (allModifiers.length === 0) {
    return (
      <p style={{ color: '#64748b', fontSize: 12 }}>
        Todavía no creaste ningún grupo de opciones. Puedes crearlos en "⚙️ Modificadores" en el menú lateral
        y después volver acá a activarlos para este plato.
      </p>
    );
  }

  const toggleGroup = (modifierId) => {
    const current = selections[modifierId];
    if (current?.checked) {
      const next = { ...selections };
      delete next[modifierId];
      onChange(next);
    } else {
      onChange({ ...selections, [modifierId]: { checked: true, is_required: false, disabled_option_ids: [] } });
    }
  };

  const toggleOption = (modifierId, optionId) => {
    const cfg = selections[modifierId];
    if (!cfg) return;
    const disabled = new Set(cfg.disabled_option_ids || []);
    if (disabled.has(optionId)) disabled.delete(optionId); else disabled.add(optionId);
    onChange({ ...selections, [modifierId]: { ...cfg, disabled_option_ids: Array.from(disabled) } });
  };

  const toggleRequired = (modifierId) => {
    const cfg = selections[modifierId];
    if (!cfg) return;
    onChange({ ...selections, [modifierId]: { ...cfg, is_required: !cfg.is_required } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {allModifiers.map(mod => {
        const cfg = selections[mod.id];
        const active = !!cfg?.checked;
        return (
          <div key={mod.id} style={{
            borderRadius: 10, border: '1px solid', padding: '10px 14px',
            borderColor: active ? '#38bdf8' : '#334155',
            background: active ? 'rgba(56, 189, 248, 0.06)' : '#0f172a',
          }}>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={active} onChange={() => toggleGroup(mod.id)} style={{ width: 16, height: 16 }} />
                <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>{mod.name}</span>
              </span>
              <span style={{ color: '#64748b', fontSize: 11 }}>{mod.options?.length || 0} opciones</span>
            </label>

            {active && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #334155' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    id={`req-${mod.id}`}
                    checked={cfg.is_required}
                    onChange={() => toggleRequired(mod.id)}
                    style={{ width: 15, height: 15 }}
                  />
                  <label htmlFor={`req-${mod.id}`} style={{ color: '#94a3b8', fontSize: 12 }}>Obligatorio en este plato</label>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {mod.options?.map(opt => {
                    const included = !(cfg.disabled_option_ids || []).includes(opt.id);
                    return (
                      <label key={opt.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={included} onChange={() => toggleOption(mod.id, opt.id)} style={{ width: 14, height: 14 }} />
                          <span style={{ color: included ? '#e2e8f0' : '#64748b', fontSize: 13 }}>{opt.name}</span>
                        </span>
                        <span style={{ color: parseFloat(opt.price_adjustment) > 0 ? '#fbbf24' : '#64748b', fontSize: 11 }}>
                          {parseFloat(opt.price_adjustment) > 0 ? `+$${parseFloat(opt.price_adjustment).toFixed(2)}` : 'Incluido'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MenuPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategoryTab, setActiveCategoryTab] = useState('all');

  const [form, setForm] = useState(emptyProductForm());
  const [comboItems, setComboItems] = useState({}); // { [product_id]: quantity }
  const [modifierSelections, setModifierSelections] = useState({}); // { [modifier_id]: {checked,is_required,disabled_option_ids} }
  const [loadingProductExtras, setLoadingProductExtras] = useState(false);

  const [draggedProductId, setDraggedProductId] = useState(null);

  function emptyProductForm() {
    return {
      name: '', description: '', history: '', ingredients: '',
      price: '', cost: '', category_id: '', preparation_time_min: 15,
      image_url: '', model_3d_url: '', is_available: true, is_combo: false,
    };
  }

  useEffect(() => {
    fetchData();
    fetchModifiers();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/menu/SUC-001');
      if (res.data.status) {
        const cats = res.data.data.categories || [];
        setCategories(cats);
        const all = [];
        cats.forEach(c => {
          (c.products || []).forEach(p => all.push({ ...p, categoryName: c.name }));
        });
        setProducts(all);
      } else {
        setError(res.data.message || 'Error del servidor');
      }
    } catch (err) {
      console.error(err);
      const backendMsg = err.response?.data?.message;
      const statusCode = err.response?.status;
      if (backendMsg) setError(`Error del servidor (${statusCode}): ${backendMsg}`);
      else if (err.request && !err.response) setError('No se pudo conectar con Laravel. Verifica que el backend esté corriendo (php artisan serve).');
      else setError(`Error inesperado: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchModifiers = async () => {
    try {
      const res = await api.get('/modifiers');
      if (res.data.status) setModifiers(res.data.data || []);
    } catch (err) {
      console.error('Error cargando opciones:', err);
    }
  };

  const resetForm = () => {
    setForm(emptyProductForm());
    setComboItems({});
    setModifierSelections({});
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const combo_items = Object.entries(comboItems)
        .filter(([, qty]) => parseInt(qty) > 0)
        .map(([product_id, quantity]) => ({ product_id: parseInt(product_id), quantity: parseInt(quantity) }));

      if (form.is_combo && combo_items.length === 0) {
        alert('Selecciona al menos un producto para armar el combo');
        return;
      }

      const modifiersPayload = Object.entries(modifierSelections)
        .filter(([, cfg]) => cfg.checked)
        .map(([modifier_id, cfg]) => ({
          modifier_id: parseInt(modifier_id),
          is_required: !!cfg.is_required,
          disabled_option_ids: cfg.disabled_option_ids || [],
        }));

      const data = {
        ...form,
        branch_id: 1,
        combo_items: form.is_combo ? combo_items : [],
        modifiers: modifiersPayload,
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, data);
      } else {
        await api.post('/products', data);
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al guardar');
    }
  };

  const toggleAvailability = async (id, current) => {
    try {
      await api.put(`/products/${id}`, { is_available: !current });
      fetchData();
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await api.delete(`/products/${id}`);
      fetchData();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const duplicateProduct = async (id) => {
    try {
      await api.post(`/products/${id}/duplicate`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al duplicar el producto');
    }
  };

  const startCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = async (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name || '',
      description: product.description || '',
      history: product.history || '',
      ingredients: product.ingredients || '',
      price: product.price || '',
      cost: product.cost || '',
      category_id: product.category_id || '',
      preparation_time_min: product.preparation_time_min || 15,
      image_url: product.image_url || '',
      model_3d_url: product.model_3d_url || '',
      is_available: product.is_available !== false,
      is_combo: !!product.is_combo,
    });
    const comboMap = {};
    (product.combo_items || []).forEach(ci => { comboMap[ci.product_id] = ci.quantity || 1; });
    setComboItems(comboMap);
    setShowForm(true);

    // Cargar qué opciones tiene asignadas actualmente este producto, para precargar el picker
    setLoadingProductExtras(true);
    try {
      const res = await api.get(`/products/${product.id}/modifier-settings`);
      const map = {};
      (res.data.data || []).forEach(row => {
        map[row.modifier_id] = {
          checked: true,
          is_required: !!row.is_required,
          disabled_option_ids: row.disabled_option_ids || [],
        };
      });
      setModifierSelections(map);
    } catch (err) {
      console.error('Error cargando opciones del producto:', err);
      setModifierSelections({});
    } finally {
      setLoadingProductExtras(false);
    }
  };

  // Reordenar productos con drag & drop (solo dentro de una categoría puntual)
  const handleProductDrop = async (targetProduct) => {
    if (draggedProductId === null || draggedProductId === targetProduct.id) return;
    if (activeCategoryTab === 'all') return;

    const catProducts = products.filter(p => p.category_id === targetProduct.category_id);
    const otherProducts = products.filter(p => p.category_id !== targetProduct.category_id);

    const fromIndex = catProducts.findIndex(p => p.id === draggedProductId);
    const toIndex = catProducts.findIndex(p => p.id === targetProduct.id);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...catProducts];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    setProducts([...otherProducts, ...reordered]);
    setDraggedProductId(null);

    try {
      await api.post('/products/reorder', {
        items: reordered.map((p, idx) => ({ id: p.id, sort_order: idx })),
      });
    } catch (err) {
      alert('Error al guardar el nuevo orden');
      fetchData();
    }
  };

  if (loading) return <p style={{ color: '#94a3b8', textAlign: 'center', padding: 50 }}>Cargando...</p>;

  if (error) return (
    <div>
      <h1 style={{ color: '#e2e8f0', fontSize: 28, marginBottom: 24 }}>🍽️ Gestionar Menú</h1>
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: '#ef4444', marginBottom: 16 }}>{error}</p>
        <button onClick={fetchData} className="btn btn-primary">Reintentar</button>
      </div>
    </div>
  );

  const filteredProducts = products.filter(p => {
    const matchesCategory = activeCategoryTab === 'all' || p.category_id === activeCategoryTab;
    const matchesSearch = !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ color: '#e2e8f0', fontSize: 28 }}>🍽️ Gestionar Menú</h1>
        <button onClick={() => (showForm ? setShowForm(false) : startCreate())} className="btn btn-primary">
          {showForm ? 'Cancelar' : '+ Nuevo Plato'}
        </button>
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        ¿Necesitas crear o reordenar categorías? Andá a{' '}
        <span onClick={() => navigate('/admin/categories')} style={{ color: '#38bdf8', cursor: 'pointer', textDecoration: 'underline' }}>
          🗂️ Categorías
        </span>
        . ¿Necesitas crear nuevos grupos de opciones (ej. "Tipo de bebida")? Andá a{' '}
        <span onClick={() => navigate('/admin/modifiers')} style={{ color: '#38bdf8', cursor: 'pointer', textDecoration: 'underline' }}>
          ⚙️ Modificadores
        </span>.
      </p>

      {/* ===== Filtro por categoría (solo lectura, la gestión vive en /admin/categories) ===== */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveCategoryTab('all')}
          style={{
            padding: '8px 16px', borderRadius: 20, border: 'none',
            background: activeCategoryTab === 'all' ? '#38bdf8' : '#1e293b',
            color: activeCategoryTab === 'all' ? '#0f172a' : '#94a3b8',
            fontWeight: 600, cursor: 'pointer', fontSize: 13,
          }}
        >
          Todas ({products.length})
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategoryTab(cat.id)}
            style={{
              padding: '8px 16px', borderRadius: 20, border: 'none',
              background: activeCategoryTab === cat.id ? '#38bdf8' : '#1e293b',
              color: activeCategoryTab === cat.id ? '#0f172a' : '#94a3b8',
              fontWeight: 600, cursor: 'pointer', fontSize: 13,
            }}
          >
            {cat.icon} {cat.name} ({products.filter(p => p.category_id === cat.id).length})
          </button>
        ))}
      </div>

      {/* ===== Buscador ===== */}
      <div style={{ marginBottom: 20, maxWidth: 360 }}>
        <input
          type="text"
          placeholder="🔍 Buscar producto por nombre..."
          className="input"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ color: '#e2e8f0', marginBottom: 16 }}>
            {editingProduct ? '✏️ Editar Producto' : 'Crear Producto'}
          </h3>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <input type="text" placeholder="Nombre *" className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
            <select className="input" value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})} required>
              <option value="">Categoría *</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
            <input type="number" step="0.01" placeholder="Precio *" className="input" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required />
            <input type="number" step="0.01" placeholder="Costo" className="input" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})} />
            <input type="number" placeholder="Tiempo (min)" className="input" value={form.preparation_time_min} onChange={e => setForm({...form, preparation_time_min: e.target.value})} />

            <div style={{ gridColumn: 'span 2' }}>
              <ImageUploadButton
                value={form.image_url}
                onChange={url => setForm({ ...form, image_url: url })}
                label="Foto del plato"
                autoGenerateAR
                onModelGenerated={url => setForm(f => ({ ...f, model_3d_url: url }))}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>
                Modelo 3D para AR (se genera solo al subir la foto; podés reemplazarlo por uno profesional si tenés)
              </label>
              <input type="text" placeholder="Se completa automáticamente al subir la foto" className="input" value={form.model_3d_url} onChange={e => setForm({...form, model_3d_url: e.target.value})} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="avail" checked={form.is_available} onChange={e => setForm({...form, is_available: e.target.checked})} style={{ width: 18, height: 18 }} />
              <label htmlFor="avail" style={{ color: '#e2e8f0', fontSize: 14 }}>Disponible</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="isCombo" checked={form.is_combo} onChange={e => setForm({...form, is_combo: e.target.checked})} style={{ width: 18, height: 18 }} />
              <label htmlFor="isCombo" style={{ color: '#e2e8f0', fontSize: 14 }}>🍔 Es un combo/promoción</label>
            </div>

            {form.is_combo && (
              <div style={{ gridColumn: 'span 2', background: '#0f172a', borderRadius: 10, padding: 14 }}>
                <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 10 }}>
                  Elige qué productos incluye este combo y en qué cantidad:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto' }}>
                  {products.filter(p => !editingProduct || p.id !== editingProduct.id).map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={comboItems[p.id] !== undefined}
                          onChange={e => {
                            setComboItems(prev => {
                              const next = { ...prev };
                              if (e.target.checked) next[p.id] = 1; else delete next[p.id];
                              return next;
                            });
                          }}
                        />
                        <span style={{ color: '#e2e8f0', fontSize: 13 }}>{p.name}</span>
                        <span style={{ color: '#64748b', fontSize: 11 }}>${p.price?.toFixed(2)}</span>
                      </label>
                      {comboItems[p.id] !== undefined && (
                        <input
                          type="number"
                          min="1"
                          className="input"
                          style={{ width: 70, padding: '4px 8px' }}
                          value={comboItems[p.id]}
                          onChange={e => setComboItems(prev => ({ ...prev, [p.id]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 8 }}>
                ⚙️ Opciones para este plato (ej: término de cocción, con/sin papas)
              </label>
              {loadingProductExtras ? (
                <p style={{ color: '#64748b', fontSize: 12 }}>Cargando opciones del producto...</p>
              ) : (
                <ProductModifiersPicker
                  allModifiers={modifiers}
                  selections={modifierSelections}
                  onChange={setModifierSelections}
                />
              )}
            </div>

            <textarea placeholder="Descripción" className="input" value={form.description} onChange={e => setForm({...form, description: e.target.value})} style={{ gridColumn: 'span 2', minHeight: 60 }} />
            <textarea placeholder="Historia" className="input" value={form.history} onChange={e => setForm({...form, history: e.target.value})} style={{ gridColumn: 'span 2', minHeight: 60 }} />
            <textarea placeholder="Ingredientes" className="input" value={form.ingredients} onChange={e => setForm({...form, ingredients: e.target.value})} style={{ gridColumn: 'span 2', minHeight: 60 }} />
            <button type="submit" className="btn btn-success" style={{ gridColumn: 'span 2' }}>
              {editingProduct ? '💾 Guardar Cambios' : 'Crear Plato'}
            </button>
          </form>
        </div>
      )}

      {activeCategoryTab !== 'all' && (
        <p style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>
          🔀 Arrastra las tarjetas para cambiar el orden en que aparecen en el menú.
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {filteredProducts.map(product => (
          <div
            key={product.id}
            className="card"
            draggable={activeCategoryTab !== 'all'}
            onDragStart={() => setDraggedProductId(product.id)}
            onDragOver={e => e.preventDefault()}
            onDrop={() => handleProductDrop(product)}
            style={{
              cursor: activeCategoryTab !== 'all' ? 'grab' : 'default',
              opacity: draggedProductId === product.id ? 0.4 : 1,
            }}
          >
            <div style={{ width: '100%', height: 140, borderRadius: 10, background: product.image_url ? `url(${product.image_url}) center/cover` : '#334155', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, position: 'relative' }}>
              {!product.image_url && '🍽️'}
              {product.is_combo && (
                <span style={{ position: 'absolute', top: 8, left: 8, background: '#a855f7', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6 }}>
                  🍔 COMBO
                </span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <h4 style={{ color: '#e2e8f0', fontSize: 15 }}>{product.name}</h4>
              <span style={{ color: '#fbbf24', fontWeight: 700 }}>${product.price?.toFixed(2)}</span>
            </div>
            <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>{product.categoryName}</p>
            {product.modifiers?.length > 0 && (
              <p style={{ color: '#a855f7', fontSize: 11, marginBottom: 10 }}>
                ⚙️ {product.modifiers.map(m => m.name).join(' · ')}
              </p>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => toggleAvailability(product.id, product.is_available)} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: product.is_available ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: product.is_available ? '#10b981' : '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {product.is_available ? '✓ Disponible' : '✗ No disponible'}
              </button>
              <button onClick={() => duplicateProduct(product.id)} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: 'rgba(148,163,184,0.15)', color: '#cbd5e1', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>📋 Duplicar</button>
              <button onClick={() => startEdit(product)} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: 'rgba(56,189,248,0.1)', color: '#38bdf8', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>✏️ Editar</button>
              <button onClick={() => deleteProduct(product.id)} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>🗑️ Eliminar</button>
            </div>
          </div>
        ))}
        {filteredProducts.length === 0 && (
          <p style={{ color: '#64748b', gridColumn: '1 / -1', textAlign: 'center', padding: 30 }}>
            No hay productos que coincidan con la búsqueda/categoría.
          </p>
        )}
      </div>
    </div>
  );
};

export default MenuPage;